import { fmtMoney } from "@/lib/format";

/* Treasury Core — the reactor gauge. Purely a readout of real settled money.
   It never animates a value upward from nothing; what you see is what Stripe
   reported. When Stripe is not connected it sits OFFLINE at $0.00. */
export default function ReactorGauge({ cents = 0, currency = "AUD", online = false, label = "SETTLED" }) {
  const ringColor = online ? "var(--yb-cyan)" : "#27406f";
  const core = online ? "var(--yb-cyan)" : "var(--yb-text-muted)";
  const R = 92;
  const C = 2 * Math.PI * R;
  // arc is a fixed "charge level" gauge — show full when there is settled money,
  // a thin idle arc when empty. It encodes presence, not a fabricated amount.
  const filled = cents > 0 ? 0.72 : 0.04;

  return (
    <div className="relative mx-auto" style={{ width: 240, height: 240 }} aria-hidden="false">
      <svg viewBox="0 0 240 240" width="240" height="240">
        <defs>
          <radialGradient id="reactorGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={online ? "rgba(54,230,255,0.30)" : "rgba(39,64,111,0.25)"} />
            <stop offset="70%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <circle cx="120" cy="120" r="110" fill="url(#reactorGlow)" />

        {/* outer rotating ring with ticks */}
        <g className="reactor-ring" style={{ transformOrigin: "120px 120px" }}>
          <circle cx="120" cy="120" r="108" fill="none" stroke="var(--yb-hairline)" strokeWidth="1" strokeDasharray="2 8" />
        </g>
        <g className="reactor-ring-rev" style={{ transformOrigin: "120px 120px" }}>
          <circle cx="120" cy="120" r="100" fill="none" stroke="var(--yb-hairline-soft)" strokeWidth="1" strokeDasharray="14 10" />
        </g>

        {/* base track */}
        <circle cx="120" cy="120" r={R} fill="none" stroke="var(--yb-panel-3)" strokeWidth="8" />
        {/* charge arc */}
        <circle
          cx="120" cy="120" r={R} fill="none"
          stroke={ringColor} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${C * filled} ${C}`}
          transform="rotate(-90 120 120)"
          style={{ filter: online ? "drop-shadow(0 0 8px var(--yb-cyan))" : "none", transition: "stroke-dasharray .6s ease" }}
        />
      </svg>

      {/* center readout */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <div className="font-mono text-[10px] tracking-[0.25em] mb-1" style={{ color: "var(--yb-text-muted)" }}>
          {label}
        </div>
        <div
          className="font-mono leading-none"
          style={{ color: core, fontSize: 30, textShadow: online ? "0 0 22px rgba(54,230,255,.5)" : "none" }}
        >
          {fmtMoney(cents, { currency })}
        </div>
        <div
          className="mt-2 font-head text-[10px] tracking-[0.18em] uppercase"
          style={{ color: online ? "var(--yb-mint)" : "var(--yb-text-muted)" }}
        >
          {online ? "● Core online" : "○ Core offline"}
        </div>
      </div>
    </div>
  );
}
